import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, setAccessToken, accessToken, logout } = useContext(UserContext);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    role: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  /* ================= Load Profile ================= */
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await APIServices.get("profile", headers);
        if (res?.status === 200) {
          const data = res.data;
          setFormData({
            username: data.username || "",
            email: data.user_login || "",
            role: data.user_role || "",
          });
        } else if (res?.status === 401) {
          logout();
          navigate("/login");
        } else {
          toast.error("Failed to fetch profile");
        }
      } catch (err) {
        toast.error("Something went wrong while fetching profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [accessToken, logout, navigate]);

  /* ================= Validation ================= */
  const validate = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = "Username is required.";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= Update Profile ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // EXACTLY AS REQUESTED - only these two fields
      const payload = {
        username: formData.username.trim(),
        user_login: formData.email.trim()
      };

      // Updated endpoint as specified
      const res = await APIServices.put("profile/update-profile", payload, headers);

      if (res?.status === 200) {
        toast.success("Profile updated successfully");
      } else if (res?.status === 401) {
        logout();
        navigate("/login");
      } else {
        toast.error(res?.data?.message || "Failed to update profile");
      }
    } catch (err) {
      toast.error("Something went wrong while updating profile");
    } finally {
      setLoading(false);
    }
  };

  /* ================= Logout ================= */
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="all_content profile_page">
      <Container fluid>
        <Row className="g-4">

          {/* ================= LEFT PROFILE CARD ================= */}
          <Col lg={4}>
            <div className="white_box h-100 profile-card p-4 text-center profile_user">
              <img
                src="/images/user_img.png"
                className="img-fluid mx-auto user_img"
                alt="User"
              />

              <h2 className="font_16 font-600 clr_blue mt-3 mb-1">
                {formData.username || "User"}
              </h2>
              <p className="font_14 clr_grey mb-2">{formData.email}</p>

              <span className="badge badge-role px-3 py-2">
                {formData.role || "Role"}
              </span>

              <hr className="my-4" />

              <div className="d-grid gap-2">
                <Button
                  variant="outline-danger"
                  className="py-2"
                  onClick={handleLogout}
                >
                  <i className="fa fa-sign-out-alt me-2"></i>
                  Logout
                </Button>
              </div>
            </div>
          </Col>

          {/* ================= RIGHT PROFILE FORM ================= */}
          <Col lg={8}>
            <div className="profile-card white_box h-100 p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="font_16 font-600 clr_blue">
                  Profile Information
                </h2>
                <Button
                  className="btn sky_theme btn-sm d-flex gap-2"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  <i className="fa fa-save"></i>
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>

              <Form onSubmit={handleSubmit} noValidate>
                <Row className="g-3">

                  <Col md={6}>
                    <Form.Group className="form-floating">
                      <Form.Control
                        type="text"
                        placeholder="Username"
                        value={formData.username}
                        isInvalid={!!errors.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        disabled={loading}
                      />
                      <Form.Label>Username *</Form.Label>
                      <Form.Control.Feedback type="invalid">
                        {errors.username}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="form-floating">
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        value={formData.email}
                        isInvalid={!!errors.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        disabled={loading}
                      />
                      <Form.Label>Email *</Form.Label>
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="form-floating">
                      <Form.Control
                        type="text"
                        placeholder="Role"
                        value={formData.role}
                        disabled
                        readOnly
                      />
                      <Form.Label>Role</Form.Label>
                    </Form.Group>
                  </Col>

                </Row>
              </Form>
            </div>
          </Col>

        </Row>
      </Container>

      <ToastContainer />
    </div>
  );
};

export default ProfilePage;